// src/routes/Home.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  useMediaQuery,
  useTheme,
  IconButton,
  List,
  ListItem,
  Checkbox,
  Typography,
  Drawer,
  Fab,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AppLayout from '../../components/layout/AppLayout';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import { useAuth } from '../../hooks/useAuth';

// カード
import KPISummaryCard from './HomeComponents/KPISummaryCard';
import FlowCard from './HomeComponents/FlowCard';
import DeadlineCard from './HomeComponents/DeadlineCard';
import TipsCard from './HomeComponents/TipsCard';
import DashboardCard from './HomeComponents/DashboardCard';
import PlanCard from './HomeComponents/PlanCard';
import MediaSelectionCard from './HomeComponents/MediaSelectionCard';
import ErrorTestComponent from '../../components/ErrorTestComponent';

// 2026年UIトレンド対応スタイル
import { bentoGridItem } from '../../theme/designTokens';
import { SkeletonDashboard } from '../../components/common/SkeletonCard';

/** 日数計算関数 */
function getRemainingDays(targetDate) {
  const now = new Date();
  const diff = targetDate - now;
  const daysLeft = Math.floor(diff / (1000 * 60 * 60 * 24));
  return Math.max(0, daysLeft);
}

export default function Home() {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_API_URL;

  // --- 認証とトークン管理 -------------------------------------------------
  const { user: currentUser, loading: authLoading } = useAuth();
  const [authError, setAuthError] = useState(null);

  // ユーザーロールを取得
  const userRole = currentUser?.role || null;

  // エラー表示用のSnackbar状態
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error'
  });

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // 認証エラーの処理
  useEffect(() => {
    if (!authLoading && !currentUser) {
      setAuthError('認証エラーが発生しました。再ログインが必要です。');
    } else {
      setAuthError(null);
    }
  }, [currentUser, authLoading]);
  // ----------------------------------------------------------------------

  // (サイドバー制御はAppLayoutに統合)

  // ダミーデータ
  // 削除: FlowCardのダミーデータは不要になりました
  // const recruitPlanProgress = 60;
  // const mediaSelectionProgress = 30;
  // const totalJobPosts = 4;
  // const totalInterviews = 2;

  // システムの使い方に関するtipsデータ
  const allTipsData = [
    {
      text: '採用計画アシスタント機能で、予算と期間に最適化された採用戦略を立案してみましょう',
      link: '/plans'
    },
    {
      text: '面接サポートを使って、面接の質問項目や評価ポイントを自動生成できます',
      link: '/interview-support'
    },
    {
      text: '求人票作成機能で、魅力的で効果的な求人情報を簡単に作成しましょう',
      link: '/job-posts'
    },
    {
      text: '媒体選定機能を活用して、ターゲットに最適な採用媒体を効率的に選択できます',
      link: '/media'
    },
    {
      text: 'ダッシュボードで応募者の進捗状況を一目で確認し、採用活動を可視化しましょう',
      link: null
    },
    {
      text: '締切管理機能で重要な採用スケジュールを見逃さないよう設定しておきましょう',
      link: null
    },
    {
      text: '面接チェックシート機能を使って、面接の評価を標準化・効率化できます',
      link: '/interview-support'
    },
    {
      text: 'わからいことはAIチャットで聞いてみましょう',
      link: null
    },
    {
      text: '採用フロー機能で現在の選考状況を把握し、次のアクションを明確にしましょう',
      link: null
    },
    {
      text: '面接サポートを活用して、候補者の総合的な評価を客観的に行えます',
      link: '/interview-support'
    },
    {
      text: '複数の採用計画を比較検討して、最適なプランを選択することをおすすめします',
      link: '/plans'
    },
    {
      text: '媒体選定の結果を保存して、今後の採用活動の参考データとして活用しましょう',
      link: '/media'
    },
    {
      text: '面接サポートの質問提案を参考に、候補者の本質を見極める面接を実施できます',
      link: '/interview-support'
    },
    {
      text: '複数の媒体選定結果を比較して、最もコストパフォーマンスの良い組み合わせを見つけましょう',
      link: '/media'
    },
    {
      text: '媒体はAI人事経由で申し込むことでお得に契約できる場合があります',
      link: '/media'
    },
    {
      text: 'AI提案の採用スケジュールを基に、無理のない採用計画を立てることをおすすめします',
      link: '/plans'
    }
  ];

  // ランダムで4つのtipsを選択
  const getRandomTips = (tips, count = 4) => {
    const shuffled = [...tips].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  const [tipsData, setTipsData] = useState(getRandomTips(allTipsData));

  // 定期的にtipsを更新（オプション：必要に応じて）
  useEffect(() => {
    const interval = setInterval(() => {
      setTipsData(getRandomTips(allTipsData));
    }, 300000); // 5分ごとに更新

    return () => clearInterval(interval);
  }, []);

  const dashboardData = {
    totalApplicants: 50,
    hired: 8,
    declined: 7,
    steps: [
      { step: '書類選考', count: 20 },
      { step: '一次面接', count: 10 },
      { step: '最終面接', count: 5 },
      { step: '内定', count: 8 },
      { step: '辞退', count: 7 },
    ],
  };

  // 締め切り日
  const esDueDate = new Date(2025, 8, 15);
  const naiteiDueDate = new Date(2025, 11, 20);

  const [daysToEs, setDaysToEs] = useState(getRemainingDays(esDueDate));
  const [daysToNaitei, setDaysToNaitei] = useState(getRemainingDays(naiteiDueDate));
  const [todayStr, setTodayStr] = useState('');

  // カード配列（表示／非表示、順序、Bento Gridサイズ）
  // 6列グリッド対応: full(6列), wide(4列), medium(3列), standard(2列)
  const [cardsInOrder, setCardsInOrder] = useState([
    { id: 'kpi', label: 'KPIサマリー', visible: true, gridSize: { xs: 12 }, bentoSize: 'full' },
    { id: 'flow', label: '採用フロー全体', visible: true, gridSize: { xs: 12, md: 8 }, bentoSize: 'wide' },
    { id: 'deadline', label: '締め切り状況', visible: true, gridSize: { xs: 12, md: 4 }, bentoSize: 'standard' },
    { id: 'tips', label: 'Tips', visible: true, gridSize: { xs: 12, md: 6 }, bentoSize: 'medium' },
    { id: 'dashboard', label: '応募者ダッシュボード', visible: true, gridSize: { xs: 12, md: 6 }, bentoSize: 'medium' },
    { id: 'plan', label: '採用計画概要', visible: true, gridSize: { xs: 12, md: 6 }, bentoSize: 'medium' },
    { id: 'mediaSelection', label: '媒体選定概要', visible: true, gridSize: { xs: 12, md: 6 }, bentoSize: 'medium' },
  ]);

  // 追加: バックエンドから取得した採用計画を保持
  const [latestPlanData, setLatestPlanData] = useState(null);
  const [decidedPlanData, setDecidedPlanData] = useState(null);
  const [allDecidedPlans, setAllDecidedPlans] = useState([]);
  const [lastDecidedSelection, setLastDecidedSelection] = useState(null);
  const [allDecidedSelections, setAllDecidedSelections] = useState([]);

  // 現在の日付
  const today = new Date();
  const todayStrFormatted = format(today, 'yyyy/MM/dd', { locale: ja });
  
  // 状態管理
  const [loading, setLoading] = useState(true);
  const [deadlines, setDeadlines] = useState(null);
  const [error, setError] = useState(null);
  
  // 締め切りデータの取得
  useEffect(() => {
    // 締め切りデータの取得はDeadlineCardコンポーネント内に統合されました
    setLoading(false);
  }, []);
  
  // 締め切りの保存
  // handleSaveDeadlinesはDeadlineCardコンポーネント内に統合されました

  useEffect(() => {
    const fetchLatestPlan = async () => {
      try {
        // 通常の最新プラン取得
        try {
          const response = await axios.get(`${BACKEND_URL}/recruit_plan_assistant/last_plan`, {
            withCredentials: true,
          });
          console.log('Latest plan:', response.data);
          
          if (response.data.planData) {
            setLatestPlanData(response.data.planData);
          } else {
            setLatestPlanData(null);
          }
        } catch (err) {
          console.warn('Failed to fetch the last plan:', err);
          setLatestPlanData(null);
        }

        // 決定済みプラン取得
        try {
          const decidedResponse = await axios.get(`${BACKEND_URL}/recruit_plan_assistant/last_decided_plan`, {
            withCredentials: true,
          });
          console.log('Decided plan:', decidedResponse.data);
          
          if (decidedResponse.data?.planData && decidedResponse.data.planId !== -1) {
            setDecidedPlanData(decidedResponse.data.planData);
          } else {
            setDecidedPlanData(null);
          }
        } catch (decidedErr) {
          console.error('Error fetching last decided plan:', decidedErr);
          setDecidedPlanData(null);
        }

        // 全ての決定済みプランを取得
        try {
          const allDecidedResponse = await axios.get(`${BACKEND_URL}/recruit_plan_assistant/all_decided_plans`, {
            withCredentials: true,
          });
          console.log('All decided plans:', allDecidedResponse.data);
          
          if (Array.isArray(allDecidedResponse.data) && allDecidedResponse.data.length > 0) {
            setAllDecidedPlans(allDecidedResponse.data);
          } else {
            setAllDecidedPlans([]);
          }
        } catch (allDecidedErr) {
          console.error('Error fetching all decided plans:', allDecidedErr);
          setAllDecidedPlans([]);
        }
        
        // 最新の決定済み媒体選定を取得
        try {
          const lastDecidedSelectionResponse = await axios.get(`${BACKEND_URL}/media_selection/last_decided_selection`, {
            withCredentials: true,
          });
          console.log('Last decided media selection:', lastDecidedSelectionResponse.data);
          
          if (lastDecidedSelectionResponse.data && lastDecidedSelectionResponse.data.planData) {
            setLastDecidedSelection(lastDecidedSelectionResponse.data);
          } else {
            setLastDecidedSelection(null);
          }
        } catch (lastSelectionErr) {
          console.error('Error fetching last decided media selection:', lastSelectionErr);
          setLastDecidedSelection(null);
        }
        
        // 全ての決定済み媒体選定を取得
        try {
          const allDecidedSelectionsResponse = await axios.get(`${BACKEND_URL}/media_selection/all_decided_selections`, {
            withCredentials: true,
          });
          console.log('All decided media selections:', allDecidedSelectionsResponse.data);
          
          if (Array.isArray(allDecidedSelectionsResponse.data) && allDecidedSelectionsResponse.data.length > 0) {
            setAllDecidedSelections(allDecidedSelectionsResponse.data);
          } else {
            setAllDecidedSelections([]);
          }
        } catch (allSelectionsErr) {
          console.error('Error fetching all decided media selections:', allSelectionsErr);
          setAllDecidedSelections([]);
        }
      } catch (err) {
        console.error('Error in fetchLatestPlan:', err);
        setLatestPlanData(null);
        setDecidedPlanData(null);
        setAllDecidedPlans([]);
        setLastDecidedSelection(null);
        setAllDecidedSelections([]);
      }
    };

    fetchLatestPlan();
  }, []);

  // 設定Drawerの開閉
  const [settingsOpen, setSettingsOpen] = useState(false);

  // 初回レンダリング
  useEffect(() => {
    setTodayStr(todayStrFormatted);
    setDaysToEs(getRemainingDays(esDueDate));
    setDaysToNaitei(getRemainingDays(naiteiDueDate));
  }, []);

  // チェックボックスで表示／非表示を切り替え
  const toggleVisibility = (index) => {
    setCardsInOrder((prev) => {
      const updated = [...prev];
      updated[index].visible = !updated[index].visible;
      return updated;
    });
  };

  // 指定idのカードを返す
  const renderCard = (id) => {
    switch (id) {
      case 'kpi':
        return <KPISummaryCard />;
      case 'flow':
        return <FlowCard />;
      case 'deadline':
        return (
          <DeadlineCard
            todayStr={todayStr}
          />
        );
      case 'tips':
        return <TipsCard tipsData={tipsData} />;
      case 'dashboard':
        return <DashboardCard data={dashboardData} />;
      case 'plan':
        // 決定済みプランが複数ある場合は全て表示、なければ最新プランを表示
        return (
          <>
            {allDecidedPlans.length > 0 ? (
              allDecidedPlans.map((plan, index) => (
                <Box key={`plan-${plan.planId}`} mb={3}>
                  <PlanCard planData={plan.planData} budgetYen={plan.requirements?.budget} hideTitle={false} />
                </Box>
              ))
            ) : decidedPlanData ? (
              <PlanCard planData={decidedPlanData} budgetYen={decidedPlanData?.requirements?.budget} />
            ) : latestPlanData ? (
              <PlanCard planData={latestPlanData} budgetYen={latestPlanData?.requirements?.budget} />
            ) : (
              <PlanCard planData={null} />
            )}
          </>
        );
      case 'mediaSelection':
        // 決定済み媒体選定が複数ある場合は全て表示、なければ最新の媒体選定を表示
        return (
          <>
            {allDecidedSelections.length > 0 ? (
              allDecidedSelections.map((selection, index) => (
                <Box key={`selection-${selection.id}`} mb={3}>
                  <MediaSelectionCard selectionData={selection} />
                </Box>
              ))
            ) : lastDecidedSelection ? (
              <MediaSelectionCard selectionData={lastDecidedSelection} />
            ) : (
              <MediaSelectionCard selectionData={null} />
            )}
          </>
        );
      default:
        return null;
    }
  };



  // ロール取得中はローディングを表示
  if (userRole === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // 子アカウントの場合は情報非表示メッセージを表示
  if (userRole === 'interview_only') {
    return (
      <AppLayout>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            minHeight: '60vh',
          }}
        >
          <Typography variant="h6" color="text.secondary">
            子アカウントではこのページの情報を閲覧できません
          </Typography>
        </Box>

        {/* エラー表示用Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={snackbar.autoHideDuration || 6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
          {/** 設定ボタン */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              zIndex: 9999,
            }}
          >
            <Fab
              onClick={() => setSettingsOpen(true)}
              color="primary"
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                boxShadow: 4,
              }}
            >
              <SettingsIcon fontSize="large" />
            </Fab>
          </Box>

          {/** カード表示エリア（Bento Gridレイアウト - 6列グリッド） */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(6, 1fr)',
              },
              gridAutoRows: 'minmax(160px, auto)',
              gap: 2.5,
            }}
          >
            {cardsInOrder.map((card) => {
              if (!card.visible) return null;

              // Bento Gridサイズの適用（6列グリッド対応）
              const getBentoStyle = () => {
                switch (card.bentoSize) {
                  case 'full':
                    // 全幅（6列すべて）
                    return {
                      gridColumn: { xs: '1 / -1', sm: '1 / -1', md: '1 / -1' },
                      gridRow: 'span 1',
                    };
                  case 'wide':
                    // 幅広（6列中4列）
                    return {
                      gridColumn: { xs: '1 / -1', sm: '1 / -1', md: 'span 4' },
                      gridRow: 'span 1',
                    };
                  case 'medium':
                    // 中サイズ（6列中3列）
                    return {
                      gridColumn: { xs: '1 / -1', sm: 'span 1', md: 'span 3' },
                      gridRow: 'span 1',
                    };
                  case 'standard':
                    // 標準（6列中2列）
                    return {
                      gridColumn: { xs: '1 / -1', sm: 'span 1', md: 'span 2' },
                      gridRow: 'span 1',
                    };
                  case 'tall':
                    // 縦長（6列中2列、2行分）
                    return {
                      gridColumn: { xs: '1 / -1', sm: 'span 1', md: 'span 2' },
                      gridRow: { xs: 'span 1', md: 'span 2' },
                    };
                  case 'large':
                    // 大サイズ（6列中3列、2行分）
                    return {
                      gridColumn: { xs: '1 / -1', sm: '1 / -1', md: 'span 3' },
                      gridRow: { xs: 'span 1', md: 'span 2' },
                    };
                  default:
                    // デフォルト（6列中2列）
                    return {
                      gridColumn: { xs: '1 / -1', sm: 'span 1', md: 'span 2' },
                      gridRow: 'span 1',
                    };
                }
              };

              return (
                <Box
                  key={card.id}
                  component={motion.div}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  sx={{
                    ...getBentoStyle(),
                    minHeight: 0,
                  }}
                >
                  {renderCard(card.id)}
                </Box>
              );
            })}
          </Box>

      {/** Drawer（カード設定） */}
      <Drawer
        anchor="right"
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        PaperProps={{
          sx: { width: 360, p: 3 },
        }}
      >
        {/* ヘッダー部分 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            カード表示設定
          </Typography>
          <Typography variant="body2" color="text.secondary">
            表示するカードと順序を設定できます
          </Typography>
        </Box>

        <List sx={{ 
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1,
        }}>
          {cardsInOrder.map((card, index) => (
            <React.Fragment key={card.id}>
              <ListItem
                sx={{
                  py: 2,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                {/* 左側: カード名とチェックボックス */}
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Checkbox
                    checked={card.visible}
                    onChange={() => toggleVisibility(index)}
                    sx={{ mr: 2 }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: card.visible ? 'text.primary' : 'text.disabled',
                    }}
                  >
                    {card.label}
                  </Typography>
                </Box>

                {/* 右側: 順序変更ボタン */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (index > 0) {
                        setCardsInOrder((prev) => {
                          const updated = [...prev];
                          const [removed] = updated.splice(index, 1);
                          updated.splice(index - 1, 0, removed);
                          return updated;
                        });
                      }
                    }}
                    disabled={index === 0}
                    sx={{
                      bgcolor: 'background.default',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (index < cardsInOrder.length - 1) {
                        setCardsInOrder((prev) => {
                          const updated = [...prev];
                          const [removed] = updated.splice(index, 1);
                          updated.splice(index + 1, 0, removed);
                          return updated;
                        });
                      }
                    }}
                    disabled={index === cardsInOrder.length - 1}
                    sx={{
                      bgcolor: 'background.default',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ListItem>
              {index < cardsInOrder.length - 1 && (
                <Divider variant="middle" component="li" />
              )}
            </React.Fragment>
          ))}
        </List>

        {/* 説明部分 */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            • チェックボックスで表示/非表示を切り替えられます
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • 矢印ボタンで順序を変更できます
          </Typography>
        </Box>
      </Drawer>
      
      {/* エラー表示用Snackbar */}
              <Snackbar
          open={snackbar.open}
          autoHideDuration={snackbar.autoHideDuration || 6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
    </AppLayout>
  );
}